import { SALUTATIONS } from '../../../../lib/salutations';
import { uid } from '../../../../lib/uid';

/**
 * Test data for the References form's Autogenerate.
 *
 * **The NAME is obviously synthetic, and this generator is the one CLAUDE.md named in advance.**
 * A generated reference writes a row into `rsys_pii`, the one store whose whole purpose is
 * holding real people's identities — and a fabricated-but-realistic name sitting there is
 * indistinguishable from a real one to anyone auditing it later. "Is this row a person?" is the
 * question that store exists to make answerable, so the answer here has to be visible on sight.
 *
 * The hospital's own `sampleReference` picks from pools of real Filipino given names and
 * surnames. That is right for its model, which keeps the name in its main database; it is wrong
 * the moment the value lands in an identity store, and this is the capture where that changes.
 * `samplePersonnel` and `sampleFamilyMember` are the two that already follow this rule — the
 * test is where the value LANDS, not which generator it came from.
 *
 * **The occupation, company and address are plausible**, and that is not an inconsistency: none
 * of them leaves `rsys_`, and an unreadable one would make the generated profile worse at the one
 * job it has.
 *
 * **The contact number is from the reserved fiction block** — `+63 917 555 …`, which can never
 * reach a real handset.
 */
const OCCUPATIONS = [
  'Head Nurse',
  'Department Head',
  'Barangay Captain',
  'School Principal',
  'Senior Accountant',
  'Project Supervisor',
];

const COMPANIES = [
  'Regional Health Office',
  'Philippine Nurses Association',
  'Bagong Nayon Elementary School',
  'Municipal Government',
  'Community Health Partners',
];

const ADDRESSES = [
  'Rizal, Antipolo, Bagong Nayon',
  'Metro Manila, Quezon City, Diliman',
  'Laguna, Calamba, Real',
  'Cavite, Imus, Poblacion',
];

export interface SampleReference {
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  occupation: string;
  company: string;
  address: string;
  contactNumber: string;
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]!;
}

export function sampleReference(): SampleReference {
  const tag = uid().slice(0, 6).toUpperCase();

  return {
    /*
      Obviously synthetic on sight — see the note above. The tag is what makes a run of clicks
      legible: nothing here is unique, so an unsuffixed second click would save quietly and leave
      two identity rows nobody can tell apart.
    */
    salutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    firstName: 'Placeholder',
    middleName: 'Testcase',
    lastName: `Testrow ${tag}`,

    // Left empty rather than invented: most people have no suffix, and generating one on every
    // click would make the default look wrong.
    suffix: '',

    occupation: pick(OCCUPATIONS),
    company: pick(COMPANIES),
    address: pick(ADDRESSES),

    // The reserved fiction block, so a generated number can never reach a real handset.
    contactNumber: `+63 917 555 ${String(1000 + Math.floor(Math.random() * 9000))}`,
  };
}
